import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel, OutlinedInput, makeStyles, Link } from '@material-ui/core';
import { z } from 'zod';
import Form from '../../Common/Form';
import InputGroup from '../../Common/Form/InputGroup';
import KobeZoneFields from './KobeZoneFields';
import KobeZoneProxies from './KobeZoneProxies';
import { formatDate } from '../../../helpers/date';
import { formatToPercentage } from '../../../helpers/number';

const validationSchema = z
  .object({
    kobe_zones: z.array(
      z.object({
        zone: z.string(),
        floor: z
          .number()
          .positive('Floor must be greater than 0.')
          .nullable(),
        ceiling: z
          .number()
          .positive('Ceiling must be greater than 0.')
          .nullable(),
      })
    ),
    reason: z
      .string()
      .trim()
      .nonempty(),
  })
  .superRefine((val, ctx) => {
    val.kobe_zones.forEach((zone, index) => {
      if (zone.ceiling !== null && zone.floor > zone.ceiling) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`kobe_zones.${index}.floor`],
          message: 'Floor shouldn’t exceed ceiling.',
          fatal: true,
        });
        return z.NEVER;
      }

      if (zone.floor !== null && zone.ceiling === zone.floor) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`kobe_zones.${index}.ceiling`],
          message: 'Ceiling must be greater than floor',
          fatal: true,
        });
      }

      if (zone.zone === null && (zone.floor !== null || zone.ceiling !== null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`kobe_zones.${index}.zone`],
          message: 'Please select a zone.',
          fatal: true,
        });
        return z.NEVER;
      }

      return z.NEVER;
    });

    return z.NEVER;
  });

const DEFAULT_FORM_VALUES = {
  kobe_zones: [],
  reason: '',
};

const useStyles = makeStyles(theme => ({
  chipDisabled: {
    background: '#e7cbec',
    color: theme.palette.primary.main,
    '&:hover': {
      background: '#e7cbec',
    },
    '&:focus': {
      background: '#e7cbec',
    },
  },
  required: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
  },
  label: {
    marginBottom: theme.spacing(2),
    fontSize: '14px',
  },
  helperText: {
    fontSize: '14px',
    lineHeight: '1.2',
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(0.625),
  },
  groupError: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
    display: 'inline-block',
    marginTop: theme.spacing(2),
  },
}));

const propTypes = {
  automationData: PropTypes.shape({
    zone_kobe_included_proxies: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)),
    zone_kobe_excluded_proxies: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)),
    kobe_zones: PropTypes.arrayOf(
      PropTypes.shape({
        zone: PropTypes.string,
        floor: PropTypes.number,
        ceiling: PropTypes.number,
      })
    ),
    reason: PropTypes.string,
  }),
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSuccess: PropTypes.bool,
  kobeZones: PropTypes.arrayOf(PropTypes.string).isRequired,
  proxyEvents: PropTypes.shape({
    KOBE_ZONE: PropTypes.arrayOf(
      PropTypes.shape({
        lobsang_event_id: PropTypes.number,
        event_date: PropTypes.string,
        performer: PropTypes.string,
        rank: PropTypes.number,
        similarity: PropTypes.number,
        venue: PropTypes.string,
      })
    ),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
};
const defaultProps = {
  automationData: null,
  isError: false,
  isLoading: false,
  isSuccess: false,
  onReset: undefined,
};

const areIdsEqual = (oldDict, newDict) => {
  const oldKeys = Object.keys(oldDict).sort();
  const newKeys = Object.keys(newDict).sort();

  if (oldKeys.length !== newKeys.length) return false;
  if (!oldKeys.every((key, index) => key === newKeys[index])) return false;

  return oldKeys.every(key => {
    const oldIds = oldDict[key] || [];
    const newIds = newDict[key] || [];

    if (oldIds.length !== newIds.length) return false;

    const sortedOld = [...oldIds].sort();
    const sortedNew = [...newIds].sort();

    return sortedOld.every((id, i) => id === sortedNew[i]);
  });
};

const compareProxyEvents = (a, b) => a.rank - b.rank;

const formatProxyEvent = proxyEvent => ({
  ...proxyEvent,
  id: proxyEvent.lobsang_event_id, // Required for TransferTable.
  performer: (
    <Link
      href={`${process.env.KONAMI_URL}/event/dashboard/${proxyEvent.lobsang_event_id}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      {proxyEvent.performer}
    </Link>
  ),
  event_date: formatDate(proxyEvent.event_date),
  similarity: formatToPercentage(proxyEvent.similarity),
});

function ZoneKobeForm({ automationData, isError, isLoading, isSuccess, kobeZones, proxyEvents, onSubmit, onReset }) {
  const classes = useStyles();
  const hasKobeZones = kobeZones.length > 0;
  const [excludedProxyEventIds, setExcludedProxyEventIds] = useState(automationData?.zone_kobe_excluded_proxies ?? {});
  // Memoize the values to prevent the form from resetting during re-renders.
  const initialValues = useMemo(() => {
    if (!automationData) {
      return DEFAULT_FORM_VALUES;
    }

    // eslint-disable-next-line camelcase
    const { kobe_zones } = automationData;

    return {
      kobe_zones,
      reason: '',
    };
  }, [automationData]);

  const getIncludedProxyEvents = (zoneName, proxies) => {
    const includedEvents = proxies
      .filter(event => !excludedProxyEventIds[zoneName]?.includes(event.lobsang_event_id))
      .sort(compareProxyEvents)
      .map(formatProxyEvent);
    return includedEvents;
  };

  const handleProxyEventsChange = (zoneKey, _, excludedItems) => {
    const excludedGroupedItems = excludedItems.reduce((acc, { zone, id }) => {
      if (!acc[zone]) {
        acc[zone] = [];
      }
      acc[zone].push(id);
      return acc;
    }, {});
    const updatedValue = {
      ...excludedProxyEventIds,
      ...excludedGroupedItems,
    };
    if (excludedGroupedItems.length === 0) {
      // explicitly remove the zone from the object
      delete updatedValue[zoneKey];
    } else {
      // otherwise overwrite/insert as normal
      updatedValue[zoneKey] = excludedGroupedItems[zoneKey];
    }
    // Update the excluded proxy event IDs in the state and keep the ones from the zone we are not modifying.
    setExcludedProxyEventIds(updatedValue);
  };

  const removeZone = zoneKey => {
    const updatedValue = { ...excludedProxyEventIds };
    delete updatedValue[zoneKey];
    setExcludedProxyEventIds(updatedValue);
  };

  const handleSubmit = formValues => {
    const includedProxyEvents = Object.keys(proxyEvents).reduce((acc, key) => {
      const includedEvents = getIncludedProxyEvents(key, proxyEvents[key]);
      return { ...acc, [key]: includedEvents.map(event => event.lobsang_event_id) };
    }, {});
    onSubmit({
      ...formValues,
      zone_kobe_included_proxies: includedProxyEvents,
      zone_kobe_excluded_proxies: excludedProxyEventIds,
    });
  };

  // Update excluded proxy events when the automation data changes.
  useEffect(() => {
    if (!areIdsEqual(automationData?.zone_kobe_excluded_proxies ?? {}, excludedProxyEventIds)) {
      setExcludedProxyEventIds(automationData?.zone_kobe_excluded_proxies ?? {});
    }
  }, [automationData?.zone_kobe_excluded_proxies]);

  return (
    <Form
      initialValues={initialValues}
      isError={isError}
      isLoading={isLoading}
      isSuccess={isSuccess}
      validationSchema={validationSchema}
      values={initialValues}
      onSubmit={formValues => handleSubmit(formValues)}
      onReset={onReset}
    >
      {({ control, formState: { errors }, trigger, watch }) => (
        <>
          <InputGroup
            label={
              <>
                Select zone to set floor/ceiling and update proxies. (Optional)
                {!hasKobeZones && (
                  <span className={classes.groupError}>
                    Groups must be created to apply Kobe floor and ceiling successfully.
                  </span>
                )}
              </>
            }
          >
            <KobeZoneFields
              name="kobe_zones"
              formMethods={{ control, errors, trigger, watch }}
              kobeZones={kobeZones}
              removeProxyZone={removeZone}
            />
          </InputGroup>

          {Object.keys(proxyEvents).length > 0 && (
            <KobeZoneProxies
              name="kobe_zones"
              kobeZones={kobeZones}
              proxyEvents={proxyEvents}
              isLoading={isLoading}
              excludedProxyEventIds={excludedProxyEventIds}
              formMethods={{ control, errors, trigger, watch }}
              handleProxyEventsChange={handleProxyEventsChange}
            />
          )}

          <FormControl fullWidth>
            <FormLabel classes={{ root: classes.label }}>
              Please provide a reason for this request.
              <span className={classes.required}>* </span>
              (Required)
            </FormLabel>
            <FormHelperText className={classes.helperText}>
              Previous reason:
              <br />
              {automationData?.reason}
            </FormHelperText>

            <Controller
              name="reason"
              control={control}
              render={({ field }) => <OutlinedInput placeholder="Type text here." rows={3} multiline {...field} />}
            />
          </FormControl>
        </>
      )}
    </Form>
  );
}

ZoneKobeForm.propTypes = propTypes;
ZoneKobeForm.defaultProps = defaultProps;

export default ZoneKobeForm;
